import { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { getCurrencyOptions } from '../../utils/currency'
import { getAllTimezones, getDefaultTimezone } from '../../utils/timezoneUtils'

const DEFAULT_FORM = {
  websiteId: '',
  eventName: '',
  eventDescription: '',
  eventCategory: 'Concert',
  eventBanner: { url: '', publicId: '' },
  shortDescription: '',
  ticketType: 'Regular',
  price: 0,
  currency: 'NGN',
  totalQuantity: 100,
  maxTicketsPerUser: 5,
  salesStartDate: '',
  salesEndDate: '',
  eventDate: '',
  startTime: '',
  endTime: '',
  doorsOpenTime: '',
  venueName: '',
  venueAddress: '',
  city: '',
  country: '',
  eventType: 'Physical',
  eventLink: '',
  organizerName: '',
  timezone: getDefaultTimezone(),
  organizerEmail: '',
  organizerPhone: '',
  features: [],
  speakers: [],
  additionalInfo: '',
  salesOpen: true,
  visibilityEnabled: true,
  status: 'active',
  taxEnabled: false,
  taxPercentage: 0,
  taxInclusive: false
}

export default function TicketsManagement({ website }) {
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(DEFAULT_FORM)
  const [editId, setEditId] = useState(null)
  const [showForm, setShowForm] = useState(false)

  const websiteId = website?._id
  const timezoneOptions = useMemo(() => getAllTimezones(), [])

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
    if (!validTypes.includes(file.type)) {
      toast.error('Ticket banner must be an image (JPG, PNG, GIF, WebP, SVG).')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Ticket banner image must be less than 10MB.')
      return
    }

    try {
      const base64 = await convertFileToBase64(file)
      setForm((prev) => ({
        ...prev,
        eventBanner: {
          url: base64,
          publicId: ''
        }
      }))
      toast.success('Ticket banner selected successfully')
    } catch (err) {
      console.error('[TicketsManagement] handleBannerUpload error', err)
      toast.error('Failed to process ticket banner')
    }
  }

  const removeBanner = () => {
    setForm((prev) => ({
      ...prev,
      eventBanner: { url: '', publicId: '' }
    }))
  }

  console.log('[TicketsManagement] Component initialized with website:', website)
  console.log('[TicketsManagement] Website ID:', websiteId)
  console.log('[TicketsManagement] Website object:', { id: website?._id, name: website?.name })

  useEffect(() => {
    if (!websiteId) return
    fetchTickets()
  }, [websiteId])

  const fetchTickets = async () => {
    try {
      setLoading(true)
      console.log('[TicketsManagement] Fetching tickets for websiteId:', websiteId)
      const response = await axios.get(`/event-tickets/website/${websiteId}`)
      console.log('[TicketsManagement] Fetch response:', response.data)
      setTickets(response.data.tickets || [])
    } catch (error) {
      console.error('[TicketsManagement] fetchTickets error', error)
      console.error('[TicketsManagement] Error details:', error.response?.data)
      const errorMessage = error.response?.data?.message || 
                          (error.response?.status === 401 ? 'Authentication required. Please login again.' :
                           error.response?.status === 403 ? 'Access denied. You may not have permission to manage tickets for this website.' :
                           'Unable to load tickets')
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const addFeature = () => {
    if (!form.newFeature?.trim()) return
    setForm((prev) => ({
      ...prev,
      features: [...(prev.features || []), prev.newFeature.trim()],
      newFeature: ''
    }))
  }

  const removeFeature = (index) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.filter((_, idx) => idx !== index)
    }))
  }

  const startEdit = (ticket) => {
    setEditId(ticket._id)
    setForm({
      ...ticket,
      eventDate: ticket.eventDate || '',
      salesStartDate: ticket.salesStartDate ? new Date(ticket.salesStartDate).toISOString().split('T')[0] : '',
      salesEndDate: ticket.salesEndDate ? new Date(ticket.salesEndDate).toISOString().split('T')[0] : '',
      eventBanner: ticket.eventBanner || { url: '', publicId: '' },
      features: ticket.features || [],
      speakers: ticket.speakers || [],
      timezone: ticket.timezone || getDefaultTimezone()
    })
    setShowForm(true)
  }

  const resetForm = () => {
    setEditId(null)
    setForm(DEFAULT_FORM)
    setShowForm(false)
  }

  const saveTicket = async (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    
    console.log('[TicketsManagement] Save button clicked', { websiteId, editId })
    
    if (!website || !website._id) {
      console.error('[TicketsManagement] Missing website', { website })
      toast.error('Website not loaded. Please refresh the page.')
      return
    }
    
    if (!websiteId) {
      console.error('[TicketsManagement] websiteId is null/undefined')
      return toast.error('Website context missing')
    }

    // Client-side validation for required fields
    const requiredFields = [
      'eventName', 'price', 'totalQuantity', 'salesStartDate', 'salesEndDate',
      'eventDate', 'startTime', 'endTime', 'venueName', 'venueAddress', 'city', 'country'
    ]

    const missingFields = requiredFields.filter(field => {
      const value = form[field]
      return !value || (typeof value === 'string' && value.trim() === '')
    })

    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`)
      return
    }

    // Validate dates
    const salesStart = new Date(form.salesStartDate)
    const salesEnd = new Date(form.salesEndDate)
    const eventDate = new Date(form.eventDate)

    if (salesStart >= salesEnd) {
      toast.error('Sales end date must be after sales start date')
      return
    }

    if (eventDate < salesStart) {
      toast.error('Event date cannot be before sales start date')
      return
    }

    // Validate price
    if (form.price <= 0) {
      toast.error('Ticket price must be greater than 0')
      return
    }

    // Validate quantity
    if (form.totalQuantity <= 0) {
      toast.error('Total quantity must be greater than 0')
      return
    }

    const payload = { ...form, websiteId }
    console.log('[TicketsManagement] Saving ticket with payload:', payload)

    setSaving(true)
    try {
      if (editId) {
        console.log('[TicketsManagement] Updating ticket:', editId)
        await axios.put(`/event-tickets/${editId}`, payload)
        toast.success('✅ Ticket updated successfully')
      } else {
        console.log('[TicketsManagement] Creating new ticket')
        const response = await axios.post('/event-tickets/create', payload)
        console.log('[TicketsManagement] Create response:', response.data)
        toast.success('✅ Ticket created successfully')
      }

      resetForm()
      await fetchTickets()
    } catch (error) {
      console.error('[TicketsManagement] saveTicket error', error)
      console.error('[TicketsManagement] Save error details:', error.response?.data)
      console.error('[TicketsManagement] Full error:', error)
      
      let errorMessage = 'Unable to save ticket'
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.missingFields) {
        errorMessage = `Missing fields: ${error.response.data.missingFields.join(', ')}`
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data. Please check all fields.'
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please login again.'
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. You may not have permission to manage tickets.'
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again or contact support.'
      } else if (error.message === 'Network Error') {
        errorMessage = 'Network error. Check your connection and try again.'
      }
      
      toast.error('❌ ' + errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const deleteTicket = async (id) => {
    if (!window.confirm('Delete this ticket?')) return
    try {
      await axios.delete(`/event-tickets/${id}`)
      toast.success('Ticket deleted')
      fetchTickets()
    } catch (error) {
      console.error('[TicketsManagement] deleteTicket error', error)
      toast.error('Unable to delete ticket')
    }
  }

  const toggleSales = async (ticket, value) => {
    try {
      await axios.put(`/event-tickets/${ticket._id}`, { salesOpen: value })
      fetchTickets()
    } catch (error) {
      console.error('[TicketsManagement] toggleSales error', error)
      toast.error('Unable to update ticket sales status')
    }
  }

  const toggleVisibility = async (ticket, value) => {
    try {
      await axios.put(`/event-tickets/${ticket._id}`, { visibilityEnabled: value })
      fetchTickets()
    } catch (error) {
      console.error('[TicketsManagement] toggleVisibility error', error)
      toast.error('Unable to update visibility')
    }
  }

  const toggleStatus = async (ticket, value) => {
    try {
      await axios.put(`/event-tickets/${ticket._id}`, { status: value })
      fetchTickets()
    } catch (error) {
      console.error('[TicketsManagement] toggleStatus error', error)
      toast.error('Unable to update ticket status')
    }
  }

  const filteredTickets = useMemo(() => tickets.sort((a, b) => (a.eventDate || '').localeCompare(b.eventDate || '')), [tickets])

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">🎫 Ticket Management</h3>
        <button
          type="button"
          onClick={() => setShowForm((prev) => !prev)}
          className="btn btn-primary"
        >
          {showForm ? 'Hide Form' : editId ? 'Edit Ticket' : 'Add New Ticket'}
        </button>
      </div>

      {showForm && (
        <div className="space-y-6 mb-6 bg-gray-50 p-4 rounded-lg">
          {/* EVENT INFORMATION SECTION */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">📋 Event Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Name *</label>
                <input
                  name="eventName"
                  value={form.eventName}
                  onChange={handleInput}
                  placeholder="e.g., Summer Music Festival"
                  className="input-field w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Category *</label>
                <select
                  name="eventCategory"
                  value={form.eventCategory}
                  onChange={handleInput}
                  className="input-field w-full"
                  required
                >
                  <option value="Concert">Concert</option>
                  <option value="Conference">Conference</option>
                  <option value="Party">Party</option>
                  <option value="Webinar">Webinar</option>
                  <option value="Workshop">Workshop</option>
                  <option value="Seminar">Seminar</option>
                  <option value="Festival">Festival</option>
                  <option value="Comedy">Comedy</option>
                  <option value="Sports">Sports</option>
                  <option value="Theater">Theater</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Description</label>
                <textarea
                  name="eventDescription"
                  value={form.eventDescription}
                  onChange={handleInput}
                  placeholder="Describe your event..."
                  className="input-field w-full"
                  rows="2"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Banner</label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBannerUpload}
                    className="w-full"
                  />
                  {form.eventBanner?.url && (
                    <button
                      type="button"
                      onClick={removeBanner}
                      className="px-3 py-1 bg-red-500 text-white rounded"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {form.eventBanner?.url && (
                  <img
                    src={form.eventBanner.url}
                    alt="Ticket banner preview"
                    className="mt-2 h-32 w-full object-cover rounded border"
                  />
                )}
              </div>
            </div>
          </div>

          {/* TICKET CONFIGURATION SECTION */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">🎫 Ticket Configuration</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Type *</label>
                <select
                  name="ticketType"
                  value={form.ticketType}
                  onChange={handleInput}
                  className="input-field w-full"
                  required
                >
                  <option value="Regular">Regular</option>
                  <option value="VIP">VIP</option>
                  <option value="Early Bird">Early Bird</option>
                  <option value="Standard">Standard</option>
                  <option value="Premium">Premium</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Price *</label>
                <input
                  type="number"
                  name="price"
                  value={form.price}
                  onChange={handleInput}
                  placeholder="0.00"
                  className="input-field w-full"
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency *</label>
                <select
                  name="currency"
                  value={form.currency}
                  onChange={handleInput}
                  className="input-field w-full"
                  required
                >
                  {getCurrencyOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Quantity Available *</label>
                <input
                  type="number"
                  name="totalQuantity"
                  value={form.totalQuantity}
                  onChange={handleInput}
                  placeholder="500"
                  className="input-field w-full"
                  required
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Tickets Per User *</label>
                <input
                  type="number"
                  name="maxTicketsPerUser"
                  value={form.maxTicketsPerUser}
                  onChange={handleInput}
                  placeholder="5"
                  className="input-field w-full"
                  required
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sales Start Date *</label>
                <input
                  type="date"
                  name="salesStartDate"
                  value={form.salesStartDate}
                  onChange={handleInput}
                  className="input-field w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sales End Date *</label>
                <input
                  type="date"
                  name="salesEndDate"
                  value={form.salesEndDate}
                  onChange={handleInput}
                  className="input-field w-full"
                  required
                />
              </div>
            </div>
          </div>

          {/* DATE & TIME SECTION */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">⏰ Date & Time</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Date *</label>
                <input
                  type="date"
                  name="eventDate"
                  value={form.eventDate}
                  onChange={handleInput}
                  className="input-field w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                <input
                  type="time"
                  name="startTime"
                  value={form.startTime}
                  onChange={handleInput}
                  className="input-field w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
                <input
                  type="time"
                  name="endTime"
                  value={form.endTime}
                  onChange={handleInput}
                  className="input-field w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Doors Open Time (Optional)</label>
                <input
                  type="time"
                  name="doorsOpenTime"
                  value={form.doorsOpenTime}
                  onChange={handleInput}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Timezone *</label>
                <select
                  name="timezone"
                  value={form.timezone}
                  onChange={handleInput}
                  className="input-field w-full"
                  required
                >
                  {timezoneOptions.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* LOCATION SECTION */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">📍 Location & Event Type</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Venue Name *</label>
                <input
                  name="venueName"
                  value={form.venueName}
                  onChange={handleInput}
                  placeholder="e.g., Madison Square Garden"
                  className="input-field w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Venue Address *</label>
                <input
                  name="venueAddress"
                  value={form.venueAddress}
                  onChange={handleInput}
                  placeholder="Street address"
                  className="input-field w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <input
                  name="city"
                  value={form.city}
                  onChange={handleInput}
                  placeholder="City"
                  className="input-field w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                <input
                  name="country"
                  value={form.country}
                  onChange={handleInput}
                  placeholder="Country"
                  className="input-field w-full"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Type *</label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="eventType"
                      value="Physical"
                      checked={form.eventType === 'Physical'}
                      onChange={handleInput}
                      className="mr-2"
                    />
                    <span className="text-sm">Physical Event</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="eventType"
                      value="Online"
                      checked={form.eventType === 'Online'}
                      onChange={handleInput}
                      className="mr-2"
                    />
                    <span className="text-sm">Online Event</span>
                  </label>
                </div>
              </div>
              {form.eventType === 'Online' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Event Link</label>
                  <input
                    name="eventLink"
                    value={form.eventLink}
                    onChange={handleInput}
                    placeholder="https://zoom.us/... or https://meet.google.com/..."
                    className="input-field w-full"
                  />
                </div>
              )}
            </div>
          </div>

          {/* ORGANIZER DETAILS SECTION */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">👤 Organizer Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organizer Name *</label>
                <input
                  name="organizerName"
                  value={form.organizerName}
                  onChange={handleInput}
                  placeholder="Your organization name"
                  className="input-field w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organizer Email *</label>
                <input
                  type="email"
                  name="organizerEmail"
                  value={form.organizerEmail}
                  onChange={handleInput}
                  placeholder="contact@example.com"
                  className="input-field w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organizer Phone (Optional)</label>
                <input
                  name="organizerPhone"
                  value={form.organizerPhone}
                  onChange={handleInput}
                  placeholder="+1-800-123-4567"
                  className="input-field w-full"
                />
              </div>
            </div>
          </div>

          {/* STATUS SECTION */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">📊 Status</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Status *</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleInput}
                  className="input-field w-full"
                  required
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="sold-out">Sold Out</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-4 border-t">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="salesOpen"
                checked={form.salesOpen}
                onChange={handleInput}
                className="form-checkbox"
                disabled={saving}
              />
              Sales Open
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="visibilityEnabled"
                checked={form.visibilityEnabled}
                onChange={handleInput}
                className="form-checkbox"
                disabled={saving}
              />
              Visible Publicly
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="taxEnabled"
                checked={form.taxEnabled}
                onChange={handleInput}
                className="form-checkbox"
                disabled={saving}
              />
              Tax Enabled
            </label>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button 
              type="button" 
              onClick={saveTicket}
              disabled={saving}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>🔄 Saving...</>
              ) : (
                <>{editId ? 'Save Changes' : 'Create Ticket'}</>
              )}
            </button>
            <button 
              type="button" 
              onClick={resetForm}
              disabled={saving}
              className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div>Loading tickets...</div>
      ) : filteredTickets.length === 0 ? (
        <div>No tickets found.</div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map((ticket) => (
            <div key={ticket._id} className="border rounded p-3 bg-gray-50">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-lg font-semibold">{ticket.eventName} ({ticket.ticketType})</h4>
                  <p className="text-sm text-gray-600">{(() => {
                    if (ticket.eventDate) {
                      const [year, month, day] = ticket.eventDate.split('-');
                      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                      return `${monthNames[parseInt(month)-1]} ${parseInt(day)}, ${year}`;
                    }
                    return new Date(ticket.eventDate).toLocaleDateString();
                  })()} | {ticket.venueName}</p>
                  <p className="text-sm text-gray-600">{ticket.soldQuantity || 0}/{ticket.totalQuantity} sold</p>
                  <p className="text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      ticket.status === 'active' ? 'bg-green-100 text-green-800' :
                      ticket.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                      ticket.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                      ticket.status === 'sold-out' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {ticket.status}
                    </span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => startEdit(ticket)} className="btn btn-sm">Edit</button>
                  <button type="button" onClick={() => deleteTicket(ticket._id)} className="btn btn-sm btn-danger">Delete</button>
                </div>
              </div>
              <div className="pt-2 flex flex-wrap gap-2">
                <button type="button" onClick={() => toggleSales(ticket, !ticket.salesOpen)} className="btn btn-xs">{ticket.salesOpen ? 'Close Sales' : 'Open Sales'}</button>
                <button type="button" onClick={() => toggleVisibility(ticket, !ticket.visibilityEnabled)} className="btn btn-xs">{ticket.visibilityEnabled ? 'Hide' : 'Show'}</button>
                <button type="button" onClick={() => toggleStatus(ticket, ticket.status === 'active' ? 'inactive' : 'active')} className="btn btn-xs">
                  {ticket.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
